import { DateTime } from "luxon";
import { normalizeEnd } from "@/components/DayViewGaps/utils";
import { getStaffingDate, isAffectByDayStart, parseTimeString } from "@/utils/dateUtils";

export const mainElementsColor = "var(--inhouse-primary)";

export enum ShiftFilterType {
	All = "All",
	recurringShift = "Recurring Shifts",
	/**
	 * Overflow bucket for a caller that offers a CAPPED set of exact shift-time windows (the job-centric
	 * Monthly view): "every shift outside the windows on offer", so none goes uncounted. Opt-in via
	 * `TimeFilterChips`' `allowFilterOtherShifts`; `isSelectedTimeFrameValidForShiftFilter` matches
	 * nothing for it, because "outside the offered set" is only meaningful to the caller that defined
	 * that set — it resolves the bucket itself (`resolveShiftWindowKey`).
	 */
	Other = "Other",
}

export type ExactShiftFilterType = {
	start: DateTime;
	end: DateTime;
};

export const isSelectedTimeFrameValidForShiftFilter = ({
	startTime,
	endTime,
	isRecurringShift,
	selectedShiftFilter,
	allowOverlap = false,
	dayStart,
}: {
	startTime: DateTime;
	endTime?: DateTime;
	selectedShiftFilter?: ShiftFilterType | ExactShiftFilterType | (ShiftFilterType | ExactShiftFilterType)[];
	isRecurringShift?: boolean;
	allowOverlap?: boolean;
	dayStart: string;
}) => {
	const staffingDate = getStaffingDate(startTime, dayStart);
	const { hour: dayStartHour } = parseTimeString(dayStart);
	const startOfDayShift = staffingDate.startOf("day").set({ hour: dayStartHour });

	/**
	 * edge case of start time that comes before start of day - should add 1 day to it because that is technically considered an overnight shift
	 */
	const actualizedStartTime = isAffectByDayStart(staffingDate, startTime.hour, startTime.minute, dayStart);
	const actualizedEndTime = endTime ? (endTime <= actualizedStartTime ? endTime.plus({ day: 1 }) : endTime) : null;

	const overlapsWithinSlack = (shiftStart: DateTime, shiftEnd: DateTime | null, winStart: DateTime, winEnd: DateTime, slackHours = 1) => {
		const end = shiftEnd?.startOf("hour") ?? winEnd; // open-ended shift: treat as covering up to window end
		const overlapStart = DateTime.max(shiftStart.startOf("hour"), winStart);
		const overlapEnd = DateTime.min(end, winEnd);
		const overlapMs = Math.max(0, overlapEnd.diff(overlapStart, "millisecond").milliseconds);
		const windowMs = winEnd.diff(winStart, "millisecond").milliseconds;
		const slackMs = slackHours * 60 * 60 * 1000;
		// require overlap to be at least window - 1h (i.e., at most 1h total outside)
		return overlapMs >= Math.max(0, windowMs - slackMs);
	};

	const checkFilterAgainstTimes = (filterToTest: ShiftFilterType | ExactShiftFilterType) => {
		const inWindowStrict = (wStart: DateTime, wEnd: DateTime) =>
			actualizedStartTime.startOf("hour") >= wStart &&
			actualizedStartTime.startOf("hour") < wEnd &&
			(!actualizedEndTime || (actualizedEndTime.startOf("hour") > wStart && actualizedEndTime.startOf("hour") <= wEnd));

		const inWindowWith1hSlack = (wStart: DateTime, wEnd: DateTime) => overlapsWithinSlack(actualizedStartTime, actualizedEndTime, wStart, wEnd, 1);

		if (filterToTest === ShiftFilterType.All) return true;
		if (typeof filterToTest !== "object") return false;

		const startToTest = isAffectByDayStart(staffingDate, filterToTest.start.hour, 0, dayStart);
		const endToTest = startOfDayShift.set({ hour: filterToTest.end.hour });
		const endToTestNormalized = normalizeEnd(startToTest, endToTest);

		const inSlack = inWindowWith1hSlack(startToTest, endToTestNormalized);
		const inStrict = inWindowStrict(startToTest, endToTestNormalized);
		return allowOverlap ? inStrict || inSlack : inStrict;
	};

	const isArray = Array.isArray(selectedShiftFilter);
	const isThereRecurringShiftFilter = isArray
		? selectedShiftFilter.includes(ShiftFilterType.recurringShift)
		: selectedShiftFilter === ShiftFilterType.recurringShift;
	return (
		!selectedShiftFilter ||
		((!isThereRecurringShiftFilter || (isThereRecurringShiftFilter && (isRecurringShift === undefined || isRecurringShift))) &&
			(isArray ? selectedShiftFilter.some((filter) => checkFilterAgainstTimes(filter)) : checkFilterAgainstTimes(selectedShiftFilter)))
	);
};
